import type { TRPC } from '../contexts/TRPC';
import { SumsubEventType } from '../types/sumsub';

import { captureAndReportError } from '../utils/errorReporting';

interface EventQueueItem {
  sessionId: string;
  eventType: SumsubEventType;
  payload: unknown;
  kybSubmissionId?: string;
  applicantId?: string;
  levelName?: string;
  clientTimestamp: string;
}

export class SumsubEventTracker {
  private sessionId: string;
  private levelName?: string;
  private eventQueue: EventQueueItem[] = [];
  private isProcessing = false;
  private batchTimer: NodeJS.Timeout | null = null;
  private readonly BATCH_SIZE = 10;
  private readonly BATCH_DELAY = 1000;

  constructor(
    private trpc: TRPC['client'],
    levelName?: string,
    private kybSubmissionId?: string,
  ) {
    this.sessionId = this.generateSessionId();
    this.levelName = levelName;
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  public getSessionId(): string {
    return this.sessionId;
  }

  public async trackEvent(
    eventType: SumsubEventType,
    payload: unknown,
  ): Promise<void> {
    const event: EventQueueItem = {
      sessionId: this.sessionId,
      eventType,
      payload,
      kybSubmissionId: this.kybSubmissionId,
      applicantId: (payload as { applicantId?: string }).applicantId,
      levelName: this.levelName,
      clientTimestamp: new Date().toISOString(),
    };

    this.eventQueue.push(event);

    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }

    // If we have enough events, process immediately
    if (this.eventQueue.length >= this.BATCH_SIZE) {
      await this.processBatch();
    } else {
      this.batchTimer = setTimeout(() => {
        this.processBatch();
      }, this.BATCH_DELAY);
    }
  }

  private async processBatch(): Promise<void> {
    if (this.isProcessing || this.eventQueue.length === 0) {
      return;
    }

    this.isProcessing = true;
    const batch = this.eventQueue.splice(0, this.BATCH_SIZE);

    try {
      await Promise.all(
        batch.map(async (event) => {
          try {
            await this.trpc.trackSumsubEvent.mutate(event);
          } catch (error) {
            captureAndReportError('Failed to track Sumsub event:', error);
            this.eventQueue.unshift(event);
          }
        }),
      );
    } finally {
      this.isProcessing = false;

      if (this.eventQueue.length > 0) {
        await this.processBatch();
      }
    }
  }

  public async flush(): Promise<void> {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    while (this.eventQueue.length > 0) {
      await this.processBatch();
    }
  }

  public createEventHandlers() {
    return {
      'idCheck.onReady': (payload: unknown) => {
        console.log('WebSDK ready:', payload);
        this.trackEvent(SumsubEventType.OnReady, payload);
      },
      'idCheck.onInitialized': (payload: unknown) => {
        console.log('WebSDK initialized:', payload);
        this.trackEvent(SumsubEventType.OnInitialized, payload);
      },
      'idCheck.onStepInitiated': (payload: unknown) => {
        console.log('Verification step initiated:', payload);
        this.trackEvent(SumsubEventType.OnStepInitiated, payload);
      },
      'idCheck.onStepCompleted': (payload: unknown) => {
        console.log('Verification step completed:', payload);
        this.trackEvent(SumsubEventType.OnStepCompleted, payload);
      },
      'idCheck.onLivenessCompleted': (payload: unknown) => {
        console.log('Liveness check completed:', payload);
        this.trackEvent(SumsubEventType.OnLivenessCompleted, payload);
      },
      'idCheck.onApplicantLoaded': (payload: unknown) => {
        console.log('Applicant loaded:', payload);
        this.trackEvent(SumsubEventType.OnApplicantLoaded, payload);
      },
      'idCheck.onApplicantSubmitted': (payload: unknown) => {
        console.log('Documents submitted:', payload);
        this.trackEvent(SumsubEventType.OnApplicantSubmitted, payload);
      },
      'idCheck.onApplicantResubmitted': (payload: unknown) => {
        console.log('Documents re-submitted:', payload);
        this.trackEvent(SumsubEventType.OnApplicantResubmitted, payload);
      },
      'idCheck.onApplicantStatusChanged': (payload: unknown) => {
        console.log('Applicant status changed:', payload);
        this.trackEvent(SumsubEventType.OnApplicantStatusChanged, payload);
      },
      'idCheck.onApplicantActionLoaded': (payload: unknown) => {
        console.log('Applicant action loaded:', payload);
        this.trackEvent(SumsubEventType.OnApplicantActionLoaded, payload);
      },
      'idCheck.onApplicantActionSubmitted': (payload: unknown) => {
        console.log('Applicant action submitted:', payload);
        this.trackEvent(SumsubEventType.OnApplicantActionSubmitted, payload);
      },
      'idCheck.onApplicantActionStatusChanged': (payload: unknown) => {
        console.log('Applicant action status changed:', payload);
        this.trackEvent(
          SumsubEventType.OnApplicantActionStatusChanged,
          payload,
        );
      },
      'idCheck.onApplicantActionCompleted': (payload: unknown) => {
        console.log('Applicant action completed:', payload);
        this.trackEvent(SumsubEventType.OnApplicantActionCompleted, payload);
      },
      'idCheck.moduleResultPresented': (payload: unknown) => {
        console.log('Module result presented:', payload);
        this.trackEvent(SumsubEventType.ModuleResultPresented, payload);
      },
      'idCheck.onError': (error: unknown) => {
        captureAndReportError('Verification error:', error);
        this.trackEvent(SumsubEventType.OnError, error);
      },
      'idCheck.onUploadError': (error: unknown) => {
        captureAndReportError('Upload error:', error);
        this.trackEvent(SumsubEventType.OnUploadError, error);
      },
      'idCheck.onUploadWarning': (warning: unknown) => {
        console.warn('Upload warning:', warning);
        this.trackEvent(SumsubEventType.OnUploadWarning, warning);
      },
      'idCheck.onResize': (payload: unknown) => {
        console.log('SDK resized:', payload);
        this.trackEvent(SumsubEventType.OnResize, payload);
      },
      'idCheck.onNavigationUiControlsStateChanged': (payload: unknown) => {
        console.log('Navigation UI controls state changed:', payload);
        this.trackEvent(
          SumsubEventType.OnNavigationUiControlsStateChanged,
          payload,
        );
      },
      'idCheck.onVideoIdentCallStarted': (payload: unknown) => {
        console.log('Video identification call started:', payload);
        this.trackEvent(SumsubEventType.OnVideoIdentCallStarted, payload);
      },
      'idCheck.onVideoIdentModeratorJoined': (payload: unknown) => {
        console.log('Video identification moderator joined:', payload);
        this.trackEvent(SumsubEventType.OnVideoIdentModeratorJoined, payload);
      },
      'idCheck.onVideoIdentCompleted': (payload: unknown) => {
        console.log('Video identification completed:', payload);
        this.trackEvent(SumsubEventType.OnVideoIdentCompleted, payload);
      },
    };
  }
}
